// @ts-check
/**
 * Student Dashboard API
 *
 * Response Types
 * @typedef {import('../../../shared/api/types').StudentDashboardResponse} StudentDashboardResponse
 * @typedef {import('../../../shared/api/types').TodoCountResponse} TodoCountResponse
 * @typedef {import('../../../shared/api/types').VocabMasteryResponse} VocabMasteryResponse
 * @typedef {import('../../../shared/api/types').CartoonUnlocksGetResponse} CartoonUnlocksGetResponse
 * @typedef {import('../../../shared/api/types').CartoonUnlocksPostResponse} CartoonUnlocksPostResponse
 * @typedef {import('../../../shared/api/types').EnrollmentStatusResponse} EnrollmentStatusResponse
 * @typedef {import('../../../shared/api/types').UnseenReviewsResponse} UnseenReviewsResponse
 * @typedef {import('../../../shared/api/types').MarkReviewsSeenResponse} MarkReviewsSeenResponse
 * @typedef {import('../../../shared/api/types').ReviewHistoryResponse} ReviewHistoryResponse
 * @typedef {import('../../../shared/api/types').QuizXpTodayResponse} QuizXpTodayResponse
 * @typedef {import('../../../shared/api/types').QuizXpTotalResponse} QuizXpTotalResponse
 * @typedef {import('../../../shared/api/types').QuizXpPostResponse} QuizXpPostResponse
 * @typedef {import('../../../shared/api/types').StudentDashboardsBatchResponse} StudentDashboardsBatchResponse
 *
 * Request Types
 * @typedef {import('../../../shared/api/types').CartoonUnlocksPostRequest} CartoonUnlocksPostRequest
 * @typedef {import('../../../shared/api/types').QuizXpPostRequest} QuizXpPostRequest
 * @typedef {import('../../../shared/api/types').StudentDashboardsBatchRequest} StudentDashboardsBatchRequest
 */

import { apiRequest } from '../../../shared/api/base';

/**
 * Get student dashboard data
 * @param {string} studentId
 * @returns {Promise<StudentDashboardResponse>}
 */
export function getStudentDashboard(studentId) {
  return apiRequest(`/students/${studentId}/dashboard`);
}

/**
 * Get todo count (lightweight endpoint for badge display)
 * @param {string} studentId
 * @returns {Promise<TodoCountResponse>}
 */
export function getTodoCount(studentId) {
  return apiRequest(`/students/${studentId}/todo-count`);
}

/**
 * Get vocab mastery data (unique assignment submissions per vocab word)
 * @param {string} studentId
 * @returns {Promise<VocabMasteryResponse>}
 */
export function getVocabMastery(studentId) {
  return apiRequest(`/students/${studentId}/vocab-mastery`);
}

/**
 * Get cartoon unlocks for a student
 * @param {string} studentId
 * @param {string} [series]
 * @returns {Promise<CartoonUnlocksGetResponse>}
 */
export function getCartoonUnlocks(studentId, series = 'southstreet') {
  return apiRequest(`/students/${studentId}/cartoon-unlocks?series=${series}`);
}

/**
 * Unlock a cartoon episode for a student
 * @param {string} studentId
 * @param {string} series
 * @param {number} episode
 * @returns {Promise<CartoonUnlocksPostResponse>}
 */
export function unlockCartoonEpisode(studentId, series, episode) {
  return apiRequest(`/students/${studentId}/cartoon-unlocks`, {
    method: 'POST',
    body: JSON.stringify({ series, episode }),
  });
}

/**
 * Get class-vs-class leaderboard (all active classes ranked by combined weekly score, with inline students)
 * @param {string} studentId
 * @returns {Promise<{ myClassIds: number[], classes: Array<{ classId: number, className: string, weeklyScore: number, students: Array<{ studentId: string, studentName: string, weeklyScore: number, totalScore: number }> }> }>}
 */
export function getClassLeaderboard(studentId) {
  return apiRequest(`/students/${studentId}/class-leaderboard`);
}

/**
 * Get enrollment status for a student (enrolled in any class or is a teacher)
 * @param {string} studentId
 * @returns {Promise<EnrollmentStatusResponse>}
 */
export function getEnrollmentStatus(studentId) {
  return apiRequest(`/students/${studentId}/enrollment-status`);
}

/**
 * Get unseen reviews for a student (reviews not yet dismissed by student)
 * @param {string} studentId
 * @returns {Promise<UnseenReviewsResponse>}
 */
export function getUnseenReviews(studentId) {
  return apiRequest(`/students/${studentId}/unseen-reviews`);
}

/**
 * Mark reviews as seen after student dismisses the modal
 * @param {string} studentId
 * @returns {Promise<MarkReviewsSeenResponse>}
 */
export function markReviewsSeen(studentId) {
  return apiRequest(`/students/${studentId}/mark-reviews-seen`, {
    method: 'POST',
  });
}

/**
 * Get all reviews (seen + unseen) for notification history modal
 * @param {string} studentId
 * @returns {Promise<ReviewHistoryResponse>}
 */
export function getReviewHistory(studentId) {
  return apiRequest(`/students/${studentId}/review-history`);
}

/**
 * Get quiz XP earned today (for practice quiz feature)
 * @param {string} studentId
 * @returns {Promise<QuizXpTodayResponse>}
 */
export function getQuizXpToday(studentId) {
  return apiRequest(`/students/${studentId}/quiz-xp-today`);
}

/**
 * Get total quiz XP earned (all time)
 * @param {string} studentId
 * @returns {Promise<QuizXpTotalResponse>}
 */
export function getQuizXpTotal(studentId) {
  return apiRequest(`/students/${studentId}/quiz-xp-total`);
}

/**
 * Award XP for correct quiz answer
 * @param {string} studentId
 * @param {number} [amount]
 * @returns {Promise<QuizXpPostResponse>}
 */
export function awardQuizXp(studentId, amount = 1) {
  return apiRequest(`/students/${studentId}/quiz-xp`, {
    method: 'POST',
    body: JSON.stringify({ amount }),
  });
}

/**
 * Get calendar assignments grouped by date for a date range
 * @param {string} studentId
 * @param {string} startDate - YYYY-MM-DD (China timezone)
 * @param {string} endDate - YYYY-MM-DD (China timezone)
 * @returns {Promise<{ assignmentsByDate: Record<string, Array> }>}
 */
export function getCalendarAssignments(studentId, startDate, endDate) {
  return apiRequest(`/students/${studentId}/calendar?startDate=${startDate}&endDate=${endDate}`);
}

/**
 * Get SRS status for all vocab words
 * @param {string} studentId
 * @returns {Promise<{ srsData: Record<string, { rLevel: number, nextReviewAt: string|null, isReviewable: boolean, wordText: string, wordType: string }> }>}
 */
export function getVocabSrs(studentId) {
  return apiRequest(`/students/${studentId}/vocab-srs`);
}

/**
 * Complete an SRS review for a word
 * @param {string} studentId
 * @param {{ masteryKey: string, wordText: string, wordType?: string, passed: boolean }} body
 * @returns {Promise<{ rLevel: number, passed: boolean, nextReviewAt: string|null }>}
 */
export function completeVocabSrsReview(studentId, body) {
  return apiRequest(`/students/${studentId}/vocab-srs/complete-review`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Seed initial SRS records for learned words
 * @param {string} studentId
 * @param {Array<{ masteryKey: string, wordText: string, wordType?: string }>} words
 * @returns {Promise<{ inserted: number }>}
 */
export function seedVocabSrs(studentId, words) {
  return apiRequest(`/students/${studentId}/vocab-srs/seed`, {
    method: 'POST',
    body: JSON.stringify({ words }),
  });
}

/**
 * Get dashboards for multiple students in a single request
 * @param {string[]} studentIds
 * @param {{ fields?: string }} [options]
 * @returns {Promise<Record<string, StudentDashboardResponse>>}
 */
export function batchGetStudentDashboards(studentIds, { fields } = {}) {
  const query = fields ? `?fields=${fields}` : '';
  return apiRequest(`/student-dashboards/batch${query}`, {
    method: 'POST',
    body: JSON.stringify({ studentIds }),
  });
}
